import { differenceInCalendarDays, parseISO } from 'date-fns';

import CalculatedStudentAssignmentStatus from 'enums/CalculatedStudentAssignmentStatus';

import { Student_Assignment_Status_Enum } from 'graphql/types/schema';

interface StudentAssignmentForStatusMatching {
  end?: string | null;
  status: Student_Assignment_Status_Enum;
  pointsEarned?: number | null;
  assignment: { graded: boolean };
}

/**
 * Determines if a student assignment matches the given assignment status filters.
 * Handles both enum statuses (Complete, Incomplete) and calculated statuses
 * (Overdue, DueToday, InProgress, NeedsGrading).
 *
 * Returns true if:
 * - No filters are applied (filteredAssignmentStatuses is empty)
 * - The assignment matches any of the selected statuses
 *
 * Returns false if:
 * - The assignment has no end date (unscheduled)
 * - hideCompleted is true and assignment is complete without needing grading
 * - The assignment doesn't match any selected status filter
 */
export function matchesAssignmentStatuses(
  studentAssignment: StudentAssignmentForStatusMatching,
  filteredAssignmentStatuses: (
    | Student_Assignment_Status_Enum
    | CalculatedStudentAssignmentStatus
  )[],
  hideCompleted: boolean
): boolean {
  if (!studentAssignment.end) {
    return false;
  }

  const studentAssignmentEnd = parseISO(studentAssignment.end);
  const daysTilDue = differenceInCalendarDays(studentAssignmentEnd, new Date());

  const studentAssignmentStatus = studentAssignment.status;

  const isOverdue = daysTilDue < 0;
  const isDueToday = daysTilDue === 0;
  const isInProgress = daysTilDue > 0;
  const needsGrading =
    studentAssignmentStatus === Student_Assignment_Status_Enum.Complete &&
    studentAssignment.pointsEarned === null &&
    studentAssignment.assignment.graded;

  if (
    hideCompleted &&
    studentAssignment.status === Student_Assignment_Status_Enum.Complete &&
    !needsGrading
  ) {
    return false;
  }

  if (filteredAssignmentStatuses.length === 0) {
    return true;
  }

  const matchesOverdueFilter =
    filteredAssignmentStatuses.includes(
      CalculatedStudentAssignmentStatus.Overdue
    ) && isOverdue;
  const matchesDueTodayFilter =
    filteredAssignmentStatuses.includes(
      CalculatedStudentAssignmentStatus.DueToday
    ) && isDueToday;
  const matchesInProgressFilter =
    filteredAssignmentStatuses.includes(
      CalculatedStudentAssignmentStatus.InProgress
    ) && isInProgress;
  const matchesNeedsGradingFilter =
    filteredAssignmentStatuses.includes(
      CalculatedStudentAssignmentStatus.NeedsGrading
    ) && needsGrading;
  const matchesCompleteFilter =
    !needsGrading &&
    filteredAssignmentStatuses.includes(
      Student_Assignment_Status_Enum.Complete
    ) &&
    studentAssignmentStatus === Student_Assignment_Status_Enum.Complete;
  const matchesIncompleteFilter =
    filteredAssignmentStatuses.includes(
      Student_Assignment_Status_Enum.Incomplete
    ) && studentAssignmentStatus === Student_Assignment_Status_Enum.Incomplete;

  return (
    matchesOverdueFilter ||
    matchesDueTodayFilter ||
    matchesInProgressFilter ||
    matchesNeedsGradingFilter ||
    matchesCompleteFilter ||
    matchesIncompleteFilter
  );
}
